import React, { useState } from 'react';
import { motion, AnimatePresence, PanInfo } from 'framer-motion';
import { Product } from '../types';

interface InteractiveBundleProps {
  items: Product[];
  className?: string;
  exitDirection?: 'left' | 'right' | 'up' | 'down';
}

const InteractiveBundle: React.FC<InteractiveBundleProps> = ({ 
  items, 
  className = '', 
  exitDirection = 'right' 
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);

  if (!items || items.length === 0) return null;

  // We show 3 cards at a time to create the stack effect
  const visibleItems = [
    items[currentIndex % items.length],
    items[(currentIndex + 1) % items.length],
    items[(currentIndex + 2) % items.length],
  ];

  const handleNext = () => {
    setCurrentIndex((prev) => prev + 1);
  };

  const handleDragEnd = (e: any, info: PanInfo) => {
    // If dragged enough horizontally or vertically, go to next
    if (Math.abs(info.offset.x) > 50 || Math.abs(info.offset.y) > 50) {
      handleNext();
    }
  };

  const getExitAnimation = () => {
    switch (exitDirection) {
      case 'left': return { x: -200, opacity: 0, rotate: -20 };
      case 'right': return { x: 200, opacity: 0, rotate: 20 };
      case 'up': return { y: -200, opacity: 0, rotate: -10 };
      case 'down': return { y: 200, opacity: 0, rotate: 10 };
      default: return { x: 200, opacity: 0 };
    }
  };

  return (
    <div className={`relative perspective-1000 ${className}`}>
      <AnimatePresence mode="popLayout">
        {visibleItems.map((item, i) => {
          // i === 0 is the top card
          const isTop = i === 0;
          return (
            <motion.div
              key={`${item.id}-${currentIndex + i}`} // Force re-render for stacking logic to trigger enter/exit
              className="absolute inset-0 rounded-2xl shadow-2xl overflow-hidden cursor-pointer border border-white/10 bg-neutral-900"
              style={{ originY: 1 }}
              initial={{ 
                scale: 0.8, 
                y: 60, 
                opacity: 0,
                zIndex: 10 - i 
              }}
              animate={{
                scale: 1 - i * 0.05,
                y: i * 20,
                opacity: 1 - i * 0.2,
                zIndex: 10 - i,
              }}
              exit={getExitAnimation()}
              transition={{ 
                type: 'spring', 
                stiffness: 300, 
                damping: 20 
              }}
              drag={isTop ? true : false}
              dragConstraints={{ left: 0, right: 0, top: 0, bottom: 0 }}
              onDragEnd={isTop ? handleDragEnd : undefined}
              onClick={isTop ? handleNext : undefined}
            >
              <img 
                src={item.imageAfter || item.imageBefore} 
                alt={item.name}
                className="w-full h-full object-cover pointer-events-none"
              />
              {/* Product Info Overlay */}
              <div className="absolute bottom-0 inset-x-0 p-4 bg-gradient-to-t from-black/80 to-transparent flex justify-between items-end">
                <div className="pr-2">
                  <p className="text-white font-bold text-sm tracking-wider uppercase truncate max-w-[120px] lg:max-w-[160px]">{item.name}</p>
                  <p className="text-amber-400 text-[10px] font-mono tracking-widest uppercase">{item.category}</p>
                </div>
                <a 
                  href="#collection"
                  onClick={(e) => e.stopPropagation()} // Prevent card swipe when clicking button
                  className="bg-amber-500 hover:bg-white text-black text-[10px] font-bold px-3 py-1.5 rounded-full uppercase tracking-widest transition-colors shadow-lg active:scale-95 shrink-0"
                >
                  Explore
                </a>
              </div>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
};

export default InteractiveBundle;
